import React, { Component } from "react"
import PropTypes from "prop-types"

import {
    datePickerGenericFilter,
    enumLabelSort,
    enumOrderSort,
    enumValueSort,
    textGenericFilter,
} from "shared/search/containers/Filter/Filters"
import {
    enumGenericFilter,
    searchifySelectGenericFilter,
    toggleGenericFilter,
} from "shared/search/containers/Filter/FilterCreators"

import {
    AlertFilterableListView,
    AlertGroupFilterableListView,
    AmendmentFilterableListView,
    ArticleFilterableListView,
    AssignmentFilterableListView,
    BillFilterableListView,
    DocumentFilterableListView,
    EventFilterableListView,
    NoteFilterableListView,
    OfficialFilterableListView,
    OfficialsAndStaffEUFilterableListView,
    RegulationFilterableListView,
    StafferFilterableListView,
    SupporterFilterableListView,
    UserFilterableListView,
    VoteFilterableListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"

import {
    AlertGroupSelect,
    AlertSelect,
    AmendmentSelect,
    ArticleSelect,
    AssignmentSelect,
    BillSelect,
    ContactSelect,
    DocumentSelect,
    EventSelect,
    NoteSelect,
    OfficialSelect,
    OfficialsAndStaffSelect,
    RegulationSelect,
    StafferSelect,
    UserSelect,
    VoteSelect,
} from "app/static/frontend/selects/containers/PieSelect"

import { allRegionsExceptUS, usRegions } from "shared/imports/regionConstants"

import { MAX_BILL_SEARCHIFY_SELECTIONS } from "shared/search/constants"

// types
const defaultPropTypes = {
    // required
    filterComponent: PropTypes.func,
    filterKey: PropTypes.string,
    uniqueFilterId: PropTypes.string,
    // optional
    dataTypeLabel: PropTypes.string,
    displayKey: PropTypes.string,
    displayLabel: PropTypes.string,
    enumItems: PropTypes.array,
    noFuture: PropTypes.bool,
    permissions: PropTypes.array,
    regions: PropTypes.array,
    selectComponent: PropTypes.func,
    sortBy: PropTypes.func,
    maxSearchifySelections: PropTypes.number,
}

// date filters
export const FeedItemDatePickerStartNoFutureFilter = (props) => {
    const FilterComponent = props.filterComponent || datePickerGenericFilter
    return <FilterComponent {...props} />
}
FeedItemDatePickerStartNoFutureFilter.propTypes = defaultPropTypes
FeedItemDatePickerStartNoFutureFilter.defaultProps = {
    filterComponent: datePickerGenericFilter,
    uniqueFilterId: "feedItemDateStartNoFuture",
    filterKey: "created__gt",
    displayLabel: "Start Date",
    noFuture: true,
    helpText: "Alerts created after date selected. Excludes date selected.",
}

export const FeedItemDatePickerEndNoFutureFilter = (props) => {
    const FilterComponent = props.filterComponent || datePickerGenericFilter
    return <FilterComponent {...props} />
}
FeedItemDatePickerEndNoFutureFilter.propTypes = defaultPropTypes
FeedItemDatePickerEndNoFutureFilter.defaultProps = {
    filterComponent: datePickerGenericFilter,
    uniqueFilterId: "feedItemDateEndNoFuture",
    filterKey: "created__lt",
    displayLabel: "End Date",
    noFuture: true,
    helpText: "Alerts created before date selected. Excludes date selected.",
}

// searchify filters
export const FeedItemAlertFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={AlertFilterableListView} {...props} />
}
FeedItemAlertFilter.propTypes = defaultPropTypes
FeedItemAlertFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_alerts",
    uniqueFilterId: "feedItemAlertFilter",

    dataTypeLabel: "Alerts",
    selectComponent: AlertSelect,
}

export const FeedItemAlertGroupFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={AlertGroupFilterableListView} {...props} />
}
FeedItemAlertGroupFilter.propTypes = defaultPropTypes
FeedItemAlertGroupFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_alert_group",
    uniqueFilterId: "feedItemAlertGroupFilter",

    dataTypeLabel: "Alert Groups",
    selectComponent: AlertGroupSelect,
}

export const FeedItemAmendmentFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={AmendmentFilterableListView} {...props} />
}
FeedItemAmendmentFilter.propTypes = defaultPropTypes
FeedItemAmendmentFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_amendments",
    uniqueFilterId: "feedItemAmendmentFilter",

    dataTypeLabel: "Amendments",
    permissions: [DjangIO.app.models.PermissionType.legislative_profiles],
    regions: DjangIO.app.models.QuorumDataType.amendment.supported_regions,
    selectComponent: AmendmentSelect,
}

export const FeedItemAssignmentFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={AssignmentFilterableListView} {...props} />
}
FeedItemAssignmentFilter.propTypes = defaultPropTypes
FeedItemAssignmentFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_assignments",
    uniqueFilterId: "FeedItemAssignmentFilter",

    dataTypeLabel: "Assignments",
    permissions: [DjangIO.app.models.PermissionType.no_permission_needed],
    regions: DjangIO.app.models.QuorumDataType.assignment.supported_regions,
    selectComponent: AssignmentSelect,
}

export const FeedItemBillFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={BillFilterableListView} {...props} />
}
FeedItemBillFilter.propTypes = defaultPropTypes
FeedItemBillFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_bills",
    uniqueFilterId: "feedItemBillFilter",

    dataTypeLabel: "Bills",
    permissions: [DjangIO.app.models.PermissionType.legislative_profiles],
    regions: DjangIO.app.models.QuorumDataType.bill.supported_regions,
    selectComponent: BillSelect,
    maxSearchifySelections: MAX_BILL_SEARCHIFY_SELECTIONS,
}

export const FeedItemContactFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={SupporterFilterableListView} {...props} />
}
FeedItemContactFilter.propTypes = defaultPropTypes
FeedItemContactFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_supporters",
    uniqueFilterId: "feedItemContactFilter",

    dataTypeLabel: "Contacts",
    permissions: [DjangIO.app.models.PermissionType.custom_contacts],
    regions: DjangIO.app.models.QuorumDataType.supporter.supported_regions,
    selectComponent: ContactSelect,
}

export const FeedItemCreatorFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter

    return <FilterComponent searchifyFLV={UserFilterableListView} {...props} />
}
FeedItemCreatorFilter.propTypes = defaultPropTypes
FeedItemCreatorFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "feedItemCreatorFilter",
    filterKey: "involving_feeditem_creator",
    selectComponent: UserSelect,
    dataTypeLabel: "Creator",
    displayLabel: "Creator",
}

export const FeedItemOwnerFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter

    return <FilterComponent searchifyFLV={UserFilterableListView} {...props} />
}
FeedItemOwnerFilter.propTypes = defaultPropTypes
FeedItemOwnerFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "involving_owner",
    uniqueFilterId: "feedItemOwnerFilter",

    displayLabel: "Owner",
    dataTypeLabel: "Owner",
    selectComponent: UserSelect,
}

export const FeedItemIsDefaultFilter = (props) => {
    const FilterComponent = props.filterComponent || toggleGenericFilter
    return <FilterComponent {...props} />
}
FeedItemIsDefaultFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    displayLabel: PropTypes.string,
    trueText: PropTypes.string,
    falseText: PropTypes.string,
}
FeedItemIsDefaultFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    uniqueFilterId: "feedItemIsDefaultFilter",
    filterKey: "alert__default",
    displayLabel: "Created by Quorum",
    trueText: "Yes",
    falseText: "No",
}

export const FeedItemDocumentFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={DocumentFilterableListView} {...props} />
}
FeedItemDocumentFilter.propTypes = defaultPropTypes
FeedItemDocumentFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_documents",
    uniqueFilterId: "feedItemDocumentFilter",

    dataTypeLabel: "Documents",
    permissions: [
        DjangIO.app.models.PermissionType.official_documents,
        DjangIO.app.models.PermissionType.contacts_documents,
    ],
    regions: DjangIO.app.models.QuorumDataType.document.supported_regions,
    selectComponent: DocumentSelect,
}

export const FeedItemEventFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={EventFilterableListView} {...props} />
}
FeedItemEventFilter.propTypes = defaultPropTypes
FeedItemEventFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_events",
    uniqueFilterId: "feedItemEventFilter",

    dataTypeLabel: "Events",
    permissions: [
        DjangIO.app.models.PermissionType.legislative_profiles,
        DjangIO.app.models.PermissionType.stakeholder,
    ],
    regions: DjangIO.app.models.QuorumDataType.event.supported_regions,
    selectComponent: EventSelect,
}

export const FeedItemArticleFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={ArticleFilterableListView} {...props} />
}
FeedItemArticleFilter.propTypes = defaultPropTypes
FeedItemArticleFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_events",
    uniqueFilterId: "feedItemArticleFilter",

    dataTypeLabel: "News Articles",
    permissions: [
        DjangIO.app.models.PermissionType.media_monitoring,
        DjangIO.app.models.PermissionType.media_relations,
    ],
    regions: DjangIO.app.models.QuorumDataType.media.supported_regions,
    selectComponent: ArticleSelect,
}

export const FeedItemNoteFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={NoteFilterableListView} {...props} />
}
FeedItemNoteFilter.propTypes = defaultPropTypes
FeedItemNoteFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_notes",
    uniqueFilterId: "feedItemNoteFilter",

    dataTypeLabel: "Notes",
    permissions: [
        DjangIO.app.models.PermissionType.internal_relationship,
        DjangIO.app.models.PermissionType.external_relationship,
        DjangIO.app.models.PermissionType.external_interactions,
    ],
    regions: DjangIO.app.models.QuorumDataType.note.supported_regions,
    selectComponent: NoteSelect,
}

export const FeedItemOfficialFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={OfficialFilterableListView} {...props} />
}
FeedItemOfficialFilter.propTypes = defaultPropTypes
FeedItemOfficialFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_officials",
    uniqueFilterId: "feedItemOfficialFilter",

    dataTypeLabel: "Officials",
    permissions: [DjangIO.app.models.PermissionType.legislators],
    regions: usRegions,
    selectComponent: OfficialSelect,
}

export const FeedItemStafferFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={StafferFilterableListView} {...props} />
}
FeedItemStafferFilter.propTypes = defaultPropTypes
FeedItemStafferFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_officials",
    uniqueFilterId: "feedItemStafferFilter",
    dataTypeLabel: DjangIO.app.models.QuorumDataType.staffer.plural,
    permissions: [DjangIO.app.models.PermissionType.staffers],
    regions: usRegions,
    selectComponent: StafferSelect,
}

export const FeedItemOfficialsAndStaffFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={OfficialsAndStaffEUFilterableListView} {...props} />
}
FeedItemOfficialsAndStaffFilter.propTypes = defaultPropTypes
FeedItemOfficialsAndStaffFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_officials",
    uniqueFilterId: "feedItemOfficialsAndStaffFilter",

    dataTypeLabel: "Officials",
    permissions: [DjangIO.app.models.PermissionType.legislators],
    regions: allRegionsExceptUS,
    selectComponent: OfficialsAndStaffSelect,
}

export const FeedItemVoteFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={VoteFilterableListView} {...props} />
}
FeedItemVoteFilter.propTypes = defaultPropTypes
FeedItemVoteFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_votes",
    uniqueFilterId: "feedItemVoteFilter",

    dataTypeLabel: "Votes",
    permissions: [DjangIO.app.models.PermissionType.legislative_profiles],
    regions: DjangIO.app.models.QuorumDataType.vote.supported_regions,
    selectComponent: VoteSelect,
}

export const FeedItemRegulationFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={RegulationFilterableListView} {...props} />
}
FeedItemRegulationFilter.propTypes = defaultPropTypes
FeedItemRegulationFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    filterKey: "underlying_regulations",
    uniqueFilterId: "feedItemRegulationFilter",

    dataTypeLabel: "Regulations",
    permissions: [DjangIO.app.models.PermissionType.regulations],
    regions: DjangIO.app.models.QuorumDataType.regulation.supported_regions,
    selectComponent: RegulationSelect,
}

// enum filters
export const AlertDataTypeFilter = (props) => {
    const FilterComponent = props.filterComponent || enumGenericFilter
    return <FilterComponent {...props} />
}
AlertDataTypeFilter.propTypes = defaultPropTypes
AlertDataTypeFilter.defaultProps = {
    filterComponent: enumGenericFilter,
    filterKey: "data_type__in",
    uniqueFilterId: "alertDataTypeFilter",
    enumItems: DjangIO.app.models.QuorumDataType.region_supported_items().filter((qdt) => qdt.supports_alert),
    displayKey: "singular",
    sortBy: enumLabelSort,
}

export const FeedItemDeliveryMediumFilter = (props) => {
    const FilterComponent = props.filterComponent || enumGenericFilter
    return <FilterComponent {...props} />
}
FeedItemDeliveryMediumFilter.propTypes = defaultPropTypes
FeedItemDeliveryMediumFilter.defaultProps = {
    filterComponent: enumGenericFilter,
    filterKey: "contains_media_type",
    uniqueFilterId: "feedItemDeliveryMediumFilter",
    enumItems: DjangIO.app.userdata.alerts.types.DeliveryMedium.region_supported_items(),
    sortBy: enumLabelSort,
    useTip: false,
}

export const AlertFrequencyFilter = (props) => {
    const FilterComponent = props.filterComponent || enumGenericFilter
    return <FilterComponent {...props} />
}
AlertFrequencyFilter.propTypes = defaultPropTypes
AlertFrequencyFilter.defaultProps = {
    filterComponent: enumGenericFilter,
    filterKey: "alert__frequency__in",
    uniqueFilterId: "alertFrequencyFilter",
    enumItems: DjangIO.app.userdata.alerts.types.AlertFrequency.region_supported_items(),
    sortBy: enumValueSort,
}

export const AlertUpdateTypeFilter = (props) => {
    const FilterComponent = props.filterComponent || enumGenericFilter
    return <FilterComponent {...props} />
}
AlertUpdateTypeFilter.propTypes = defaultPropTypes
AlertUpdateTypeFilter.defaultProps = {
    filterComponent: enumGenericFilter,
    filterKey: "update_type__in",
    uniqueFilterId: "alertUpdateTypeFilter",
    enumItems: DjangIO.app.userdata.alerts.types.NewUpdateType.region_supported_items().filter(
        (item) => item.label !== DjangIO.app.userdata.alerts.types.NewUpdateType.meta_updates.label,
    ),
    displayKey: "search_label",
    sortBy: enumOrderSort,
}

export const AlertTitleFilter = (props) => {
    const FilterComponent = props.filterComponent || textGenericFilter
    return <FilterComponent {...props} />
}
AlertTitleFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    placeholderText: PropTypes.string,
    displayLabel: PropTypes.string,
}
AlertTitleFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "alertTitleFilter",
    filterKey: "title_search",
    placeholderText: "Search by title...",
    displayLabel: "Search by Alert Title",
}

export const AlertGroupTitleFilter = (props) => {
    const FilterComponent = props.filterComponent || textGenericFilter
    return <FilterComponent {...props} />
}
AlertGroupTitleFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    placeholderText: PropTypes.string,
    displayLabel: PropTypes.string,
}
AlertGroupTitleFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "alertGroupTitleFilter",
    filterKey: "group_title_search",
    placeholderText: "Search by title...",
    displayLabel: "Search by Alert Group Title",
}

export const FeedItemDescriptionFilter = (props) => {
    const FilterComponent = props.filterComponent || textGenericFilter
    return <FilterComponent {...props} />
}
FeedItemDescriptionFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    placeholderText: PropTypes.string,
    displayLabel: PropTypes.string,
}
FeedItemDescriptionFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "feedItemDescriptionFilter",
    filterKey: "description_search",
    placeholderText: "Search by description...",
    displayLabel: "Search by Alert Item Description",
}

export const FeedItemAdminFilter = (props) => {
    const FilterComponent = props.filterComponent || toggleGenericFilter
    return <FilterComponent {...props} />
}
FeedItemAdminFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    displayLabel: PropTypes.string,
    trueText: PropTypes.string,
    falseText: PropTypes.string,
}
FeedItemAdminFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    uniqueFilterId: "feedItemAdminFilter",
    filterKey: "current_user",
    trueText: "All Alerts",
    falseText: "My Alerts",
}

export const FeedItemPriorityFilter = (props) => {
    const FilterComponent = props.filterComponent || enumGenericFilter
    return <FilterComponent {...props} />
}
FeedItemPriorityFilter.propTypes = defaultPropTypes
FeedItemPriorityFilter.defaultProps = {
    filterComponent: enumGenericFilter,
    uniqueFilterId: "feedItemPriorityFilter",
    filterKey: "my_priority",
    enumItems: DjangIO.app.userdata.types.LegislativePriorityType.region_supported_items(),
    sortBy: enumValueSort,
}

// Invisible filter used for push notification linking
export class FeedItemInvisibleDeliverablesFilter extends Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "feedItemInvisibleDeliverablesFilter",
        filterKey: "deliverables",
        invisible: true,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}
